import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowRight, ChatCircleDots, CalendarCheck, Truck, Lightning, PlugsConnected,
  SlidersHorizontal, ShoppingCart, CheckCircle, Star, PaperPlaneTilt, Clock,
  Fire, Sparkle,
} from "@phosphor-icons/react";
import { LogoMark } from "@/components/Logo";

// ------------------------ Hero chat animation ------------------------
const DEMO_SCRIPT = [
  { from: "customer", text: "Hola, quiero pedir pizza 🍕", delay: 600 },
  { from: "bot", text: "¡Hola! Tenemos Pepperoni $25.000, Hawaiana $26.000 y Vegetariana $24.000. ¿Cuál prefieres?", delay: 1800 },
  { from: "customer", text: "Una Pepperoni", delay: 1500 },
  { from: "bot", text: "Perfecto, 1 Pizza Pepperoni por $25.000. ¿Para domicilio o recoger?", delay: 1500 },
  { from: "customer", text: "Domicilio, calle 72 #10-25", delay: 1600 },
  { from: "bot", text: "Listo Carlos ✅ Tu pedido queda registrado por $29.000 (incluye envío). Llega en 30-40 min 🚀", delay: 2000 },
];

function HeroChat() {
  const [visible, setVisible] = useState([]);
  const [typing, setTyping] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let cancel = false;
    const run = async () => {
      for (let i = 0; i < DEMO_SCRIPT.length; i++) {
        if (cancel) return;
        await new Promise((r) => setTimeout(r, DEMO_SCRIPT[i].delay));
        if (DEMO_SCRIPT[i].from === "bot") {
          setTyping(true);
          await new Promise((r) => setTimeout(r, 900));
          if (cancel) return;
          setTyping(false);
        }
        setVisible((v) => [...v, DEMO_SCRIPT[i]]);
      }
    };
    const timer = setTimeout(run, 400);
    return () => { cancel = true; clearTimeout(timer); };
  }, []);

  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" });
  }, [visible, typing]);

  return (
    <div className="relative w-full max-w-md mx-auto">
      <div className="absolute -inset-6 bg-gradient-to-br from-[#5E6AD2]/30 to-[#F97316]/20 blur-3xl rounded-full" />
      <div className="relative bg-[#121214] border border-[#27272A] rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
        <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center">
            <LogoMark size={28} rounded={true} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-medium truncate">La Pizza de Juan</div>
            <div className="text-[10px] text-white/80 flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> en línea · responde con IA
            </div>
          </div>
        </div>
        <div
          ref={ref}
          className="h-[380px] overflow-y-auto px-4 py-5 space-y-3"
          style={{
            background:
              "linear-gradient(180deg,#0E0E10 0%,#121214 100%)",
          }}
        >
          {visible.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.from === "customer" ? "justify-end" : "justify-start"} animate-in slide-in-from-bottom-2 fade-in duration-500`}
            >
              <div
                className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                  m.from === "customer"
                    ? "bg-[#005C4B] text-white rounded-tr-sm"
                    : "bg-[#202C33] text-[#E9EDEF] rounded-tl-sm"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {typing && (
            <div className="flex justify-start">
              <div className="bg-[#202C33] text-[#E9EDEF] rounded-2xl rounded-tl-sm px-4 py-3 inline-flex gap-1">
                <span className="h-1.5 w-1.5 bg-[#71717A] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-1.5 w-1.5 bg-[#71717A] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="h-1.5 w-1.5 bg-[#71717A] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------ Benefits ------------------------
const BENEFITS = [
  { icon: ChatCircleDots, title: "Atiende pedidos 24/7", desc: "Nunca pierdes una venta. La IA responde al instante, cualquier día y hora.", color: "#5E6AD2" },
  { icon: CalendarCheck, title: "Agenda citas sola", desc: "Captura fecha, hora y servicio, y las sincroniza con tu calendario.", color: "#F97316" },
  { icon: Truck, title: "Seguimiento de entregas", desc: "Avisa automáticamente al cliente cuando el pedido va en camino o es entregado.", color: "#22C55E" },
  { icon: Lightning, title: "Cierra sin operador", desc: "Convierte conversaciones en órdenes registradas, sin que tengas que mover un dedo.", color: "#A78BFA" },
];

function Benefits() {
  return (
    <section id="beneficios" className="py-24 px-6 max-w-7xl mx-auto">
      <div className="max-w-2xl mb-14">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#F97316] mb-3">Beneficios</div>
        <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-tight">
          Un agente de ventas que trabaja por ti
        </h2>
        <p className="text-[#A1A1AA] mt-4 leading-relaxed">
          IA conversacional entrenada en tu negocio. Vende, atiende y da seguimiento sin que necesites
          contratar más personal.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {BENEFITS.map((b, i) => (
          <div
            key={i}
            data-testid={`benefit-${i}`}
            className="group relative bg-[#121214] border border-[#27272A] rounded-xl p-6 hover:border-[#3F3F46] transition-colors"
          >
            <div
              className="h-11 w-11 rounded-lg flex items-center justify-center mb-4"
              style={{ background: `${b.color}20` }}
            >
              <b.icon size={22} weight="duotone" style={{ color: b.color }} />
            </div>
            <div className="font-display text-lg font-medium mb-2">{b.title}</div>
            <p className="text-sm text-[#A1A1AA] leading-relaxed">{b.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ------------------------ How it works ------------------------
const STEPS = [
  { icon: PlugsConnected, title: "Conecta tu WhatsApp", desc: "Vincula tu número con un par de clics. Sin cambiar tu número actual." },
  { icon: SlidersHorizontal, title: "Configura menú o servicios", desc: "Carga tu catálogo, precios y zonas. La IA aprende tu negocio en minutos." },
  { icon: ShoppingCart, title: "Recibe pedidos automáticamente", desc: "La IA conversa, cierra la venta y registra el pedido en tu panel." },
];

function HowItWorks() {
  return (
    <section id="como-funciona" className="py-24 px-6 bg-[#09090B] border-y border-[#27272A]">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-14">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5E6AD2] mb-3">Cómo funciona</div>
          <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-tight">
            3 pasos. 10 minutos. Listo.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {STEPS.map((s, i) => (
            <div
              key={i}
              data-testid={`step-${i + 1}`}
              className="relative bg-gradient-to-br from-[#121214] to-[#0E0E10] border border-[#27272A] rounded-2xl p-8"
            >
              <div className="text-7xl font-display font-bold text-[#5E6AD2]/10 absolute top-4 right-6 leading-none select-none">
                0{i + 1}
              </div>
              <div className="relative">
                <div className="h-12 w-12 rounded-xl bg-[#5E6AD2]/10 flex items-center justify-center mb-5 border border-[#5E6AD2]/30">
                  <s.icon size={22} weight="duotone" className="text-[#5E6AD2]" />
                </div>
                <div className="font-display text-xl font-medium mb-3">{s.title}</div>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ------------------------ Dashboard preview ------------------------
function DashboardPreview() {
  return (
    <section className="py-24 px-6 max-w-7xl mx-auto">
      <div className="max-w-2xl mb-14">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#22C55E] mb-3">Panel de control</div>
        <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-tight">
          Todo bajo control desde un solo lugar
        </h2>
        <p className="text-[#A1A1AA] mt-4 leading-relaxed">
          Inbox de conversaciones, pedidos, citas, comprobantes y reclamos — todo en una vista limpia que
          puedes operar desde el celular.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {[
          { title: "Inbox", desc: "Todas las conversaciones vivas, con la IA respondiendo o escalando al humano cuando aplica.", items: [
            { name: "María Fernández", m: "¿Hacen entregas en Chapinero?", t: "ahora" },
            { name: "Carlos Ríos", m: "Voy a pagar con Nequi 👍", t: "2m" },
            { name: "Laura Torres", m: "Gracias! Ya llegó 🙌", t: "15m" },
          ]},
          { title: "Pedidos", desc: "Pedidos en tiempo real con estado, total y domiciliario asignado.", items: [
            { name: "#2451 · Diego C.", m: "Pizza x2 · $54.000", t: "en camino" },
            { name: "#2450 · Ana L.", m: "Hamburguesa · $18.000", t: "preparando" },
            { name: "#2449 · Pedro M.", m: "Combo · $42.000", t: "entregado" },
          ]},
          { title: "Citas", desc: "Agenda sincronizada con tu calendario. Recordatorios automáticos al cliente.", items: [
            { name: "Corte · 14:00", m: "Juan Pérez", t: "hoy" },
            { name: "Manicura · 16:30", m: "Sofía G.", t: "hoy" },
            { name: "Masaje · 10:00", m: "Luis T.", t: "mañana" },
          ]},
        ].map((card, i) => (
          <div
            key={i}
            data-testid={`dash-preview-${i}`}
            className="bg-[#121214] border border-[#27272A] rounded-xl overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Panel</div>
                <div className="font-display font-medium text-base">{card.title}</div>
              </div>
              <Sparkle size={16} className="text-[#5E6AD2]" weight="fill" />
            </div>
            <div className="divide-y divide-[#27272A]">
              {card.items.map((it, j) => (
                <div key={j} className="px-5 py-4 hover:bg-[#1A1A1D] transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium truncate">{it.name}</div>
                    <div className="text-[10px] text-[#71717A] ml-2">{it.t}</div>
                  </div>
                  <div className="text-xs text-[#A1A1AA] truncate">{it.m}</div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 bg-[#0A0A0C] text-[11px] text-[#71717A]">{card.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ------------------------ Interactive demo ------------------------
function InteractiveDemo() {
  const [messages, setMessages] = useState([
    { from: "bot", text: "¡Hola! Soy el agente IA de Wazco Express. Escríbeme como si yo fuera el bot de tu negocio y verás cómo respondo 🤖" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef("visitor-" + Math.random().toString(36).slice(2, 10));
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const text = input.trim();
    setMessages((m) => [...m, { from: "customer", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/public/demo-chat", { session_id: sessionRef.current, message: text });
      setMessages((m) => [...m, { from: "bot", text: data.reply }]);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No pude responder. Intenta de nuevo");
    } finally {
      setLoading(false);
    }
  };

  const suggest = (s) => { setInput(s); };

  return (
    <section id="demo" className="py-24 px-6 bg-gradient-to-br from-[#09090B] via-[#0F0E1E] to-[#09090B] border-y border-[#27272A]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#A78BFA] mb-3">Demo interactiva</div>
          <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-tight mb-4">
            Pruébalo tú mismo
          </h2>
          <p className="text-[#A1A1AA]">
            Escribe como si fueras un cliente de una pizzería. El bot está entrenado en un menú de ejemplo
            para que veas la experiencia real.
          </p>
        </div>
        <div className="bg-[#121214] border border-[#27272A] rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
          <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
            <LogoMark size={28} rounded />
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium">Demo · La Pizza de Juan</div>
              <div className="text-[10px] text-white/80">prueba interactiva</div>
            </div>
          </div>
          <div
            ref={scrollRef}
            data-testid="demo-chat-box"
            className="h-[400px] overflow-y-auto px-4 py-5 space-y-3"
            style={{ background: "linear-gradient(180deg,#0E0E10 0%,#121214 100%)" }}
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "customer" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                    m.from === "customer"
                      ? "bg-[#005C4B] text-white rounded-tr-sm"
                      : "bg-[#202C33] text-[#E9EDEF] rounded-tl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#202C33] text-[#E9EDEF] rounded-2xl rounded-tl-sm px-4 py-3 inline-flex gap-1">
                  <span className="h-1.5 w-1.5 bg-[#71717A] rounded-full animate-bounce" />
                  <span className="h-1.5 w-1.5 bg-[#71717A] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 bg-[#71717A] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>
          <div className="px-4 py-3 border-t border-[#27272A] flex flex-wrap gap-2">
            {["Hola, ¿qué pizzas tienen?", "Quiero una Hawaiana", "¿Hacen domicilios en Chapinero?"].map((s) => (
              <button
                key={s}
                onClick={() => suggest(s)}
                className="text-[11px] px-3 py-1.5 rounded-full border border-[#27272A] bg-[#0A0A0C] text-[#A1A1AA] hover:text-white hover:border-[#3F3F46]"
                data-testid={`demo-suggest-${s.slice(0,10)}`}
              >{s}</button>
            ))}
          </div>
          <form onSubmit={send} className="p-4 border-t border-[#27272A] flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe un mensaje..."
              data-testid="demo-input"
              className="flex-1 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2]"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="demo-send"
              className="bg-[#5E6AD2] hover:bg-[#4F58B8] text-white"
            >
              <PaperPlaneTilt size={14} weight="fill" />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}

// ------------------------ Launch offer ------------------------
function LaunchOffer() {
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    api.get("/public/launch-offer").then((r) => setOffer(r.data)).catch(() => {});
  }, []);
  if (!offer) return null;
  const pct = Math.max(5, Math.min(100, (offer.remaining / offer.limit) * 100));
  return (
    <section className="py-16 px-6 max-w-7xl mx-auto" data-testid="launch-offer">
      <div className="relative overflow-hidden rounded-2xl border border-[#F97316]/40 bg-gradient-to-br from-[#1E0F08] via-[#120A1E] to-[#09090B] p-8 md:p-12">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(circle at 20% 30%, rgba(249,115,22,0.4), transparent 60%), radial-gradient(circle at 80% 70%, rgba(94,106,210,0.3), transparent 60%)",
          }}
        />
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#F97316] bg-[#F97316]/10 border border-[#F97316]/30 rounded-full px-3 py-1 mb-5">
              <Fire size={10} weight="fill" /> Oferta de lanzamiento
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tighter leading-tight mb-3">
              Primeros {offer.limit} negocios obtienen{" "}
              <span className="text-[#F97316]">{offer.bonus_days} días gratis</span>
            </h2>
            <p className="text-[#D4D4D8] leading-relaxed mb-6">
              Sin tarjeta, sin compromiso. Activa tu agente IA, cierra ventas por WhatsApp y decides al final del mes.
            </p>
            <Link to="/register">
              <Button
                data-testid="offer-cta-button"
                size="lg"
                className="bg-[#F97316] hover:bg-[#EA580C] text-white h-12 px-6"
              >
                Quiero mi mes gratis <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
          <div className="bg-[#121214]/70 backdrop-blur border border-[#27272A] rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-[#71717A]">Cupos disponibles</span>
              <span className="text-xs text-[#F97316] font-medium">{offer.remaining} / {offer.limit}</span>
            </div>
            <div className="h-3 rounded-full bg-[#27272A] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#F97316] to-[#FBBF24] transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3 text-center">
              <div><div className="font-display text-2xl font-medium text-[#F97316]">{offer.remaining}</div><div className="text-[10px] uppercase tracking-wider text-[#71717A]">Disponibles</div></div>
              <div><div className="font-display text-2xl font-medium text-white">{offer.taken}</div><div className="text-[10px] uppercase tracking-wider text-[#71717A]">Tomados</div></div>
              <div><div className="font-display text-2xl font-medium text-emerald-400">{offer.bonus_days}d</div><div className="text-[10px] uppercase tracking-wider text-[#71717A]">Gratis</div></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ------------------------ Pricing ------------------------
function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/public/plans")
      .then((r) => setPlans(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return (
    <section id="planes" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5E6AD2] mb-3">Planes</div>
          <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-tight mb-4">
            Precios simples, sin letra pequeña
          </h2>
          <p className="text-[#A1A1AA]">Elige el plan que se adapta a tu negocio. Cambia o cancela cuando quieras.</p>
        </div>
        {loading ? (
          <div className="text-center text-[#71717A] text-sm">Cargando planes...</div>
        ) : plans.length === 0 ? (
          <div className="text-center text-[#71717A] text-sm">Pronto publicaremos nuestros planes.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {plans.map((p) => (
              <div
                key={p.id}
                data-testid={`plan-${p.name.toLowerCase()}`}
                className={`relative bg-[#121214] rounded-2xl p-8 flex flex-col ${
                  p.highlight
                    ? "border-2 border-[#5E6AD2] shadow-[0_0_60px_rgba(94,106,210,0.3)]"
                    : "border border-[#27272A]"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#5E6AD2] text-white text-[10px] uppercase tracking-wider font-medium px-3 py-1 rounded-full">
                    Más popular
                  </div>
                )}
                <div className="font-display text-xl font-medium mb-1">{p.name}</div>
                <p className="text-xs text-[#A1A1AA] mb-5 min-h-[32px]">{p.description}</p>
                <div className="mb-6">
                  <span className="font-display text-5xl font-medium">${p.price}</span>
                  <span className="text-[#71717A] text-sm ml-2">{p.currency}/{p.period}</span>
                </div>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#D4D4D8]">
                      <CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button
                    data-testid={`plan-cta-${p.name.toLowerCase()}`}
                    className={`w-full h-11 ${
                      p.highlight
                        ? "bg-[#5E6AD2] hover:bg-[#4F58B8] text-white"
                        : "bg-[#27272A] hover:bg-[#3F3F46] text-white"
                    }`}
                  >
                    {p.cta_label || "Empezar"}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ------------------------ Contact form ------------------------
function ContactForm() {
  const [form, setForm] = useState({ name: "", business: "", phone: "", email: "", business_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/leads", form);
      setSent(true);
      toast.success("¡Gracias! Te contactamos pronto.");
      setForm({ name: "", business: "", phone: "", email: "", business_type: "", message: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No pudimos enviar. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <section id="contacto" className="py-24 px-6 border-t border-[#27272A]">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#F97316] mb-3">Contáctanos</div>
          <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-tight mb-3">
            Hablemos de tu negocio
          </h2>
          <p className="text-[#A1A1AA]">Cuéntanos qué vendes y te mostramos cómo Wazco Express encaja en tu operación.</p>
        </div>
        {sent ? (
          <div className="text-center p-10 bg-[#121214] border border-emerald-500/30 rounded-xl" data-testid="contact-sent">
            <CheckCircle size={40} weight="fill" className="text-emerald-500 mx-auto mb-3" />
            <div className="font-display text-xl mb-2">¡Mensaje enviado!</div>
            <p className="text-sm text-[#A1A1AA]">Te contactaremos por el correo o teléfono que nos dejaste.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="bg-[#121214] border border-[#27272A] rounded-xl p-6 md:p-8 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#71717A]">Nombre</Label>
                <Input required value={form.name} onChange={change("name")} data-testid="contact-name" className="mt-1.5 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2] h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#71717A]">Negocio</Label>
                <Input value={form.business} onChange={change("business")} data-testid="contact-business" className="mt-1.5 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2] h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#71717A]">Teléfono</Label>
                <Input value={form.phone} onChange={change("phone")} data-testid="contact-phone" className="mt-1.5 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2] h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#71717A]">Email</Label>
                <Input required type="email" value={form.email} onChange={change("email")} data-testid="contact-email" className="mt-1.5 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2] h-11" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#71717A]">Tipo de negocio</Label>
              <select
                value={form.business_type}
                onChange={change("business_type")}
                data-testid="contact-type"
                className="mt-1.5 w-full h-11 rounded-md bg-[#0A0A0C] border border-[#27272A] text-white px-3 focus:border-[#5E6AD2] focus:outline-none"
              >
                <option value="">Selecciona uno</option>
                <option value="restaurante">Restaurante / Comida</option>
                <option value="belleza">Belleza / Estética</option>
                <option value="tienda">Tienda / Retail</option>
                <option value="servicios">Servicios profesionales</option>
                <option value="salud">Salud / Consultorio</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#71717A]">Mensaje</Label>
              <Textarea
                value={form.message}
                onChange={change("message")}
                rows={4}
                placeholder="Cuéntanos qué vendes y qué te gustaría automatizar..."
                data-testid="contact-message"
                className="mt-1.5 bg-[#0A0A0C] border-[#27272A] focus:border-[#5E6AD2]"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="w-full h-12 bg-[#5E6AD2] hover:bg-[#4F58B8] text-white"
            >
              {submitting ? "Enviando..." : (<>Enviar mensaje <ArrowRight size={16} className="ml-2" /></>)}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}

// ------------------------ Nav + Hero + Footer ------------------------
function Nav() {
  return (
    <nav className="fixed top-0 inset-x-0 z-30 bg-[#09090B]/80 backdrop-blur border-b border-[#27272A]">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
          <LogoMark size={32} />
          <div className="font-display font-medium tracking-tight">
            <span className="text-[#5E6AD2]">Wazco</span> <span className="text-[#F97316]">Express</span>
          </div>
        </Link>
        <div className="hidden md:flex items-center gap-7 text-sm text-[#A1A1AA]">
          <a href="#beneficios" className="hover:text-white transition-colors">Beneficios</a>
          <a href="#como-funciona" className="hover:text-white transition-colors">Cómo funciona</a>
          <a href="#demo" className="hover:text-white transition-colors">Demo</a>
          <a href="#planes" className="hover:text-white transition-colors">Planes</a>
          <a href="#contacto" className="hover:text-white transition-colors">Contacto</a>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login">
            <Button variant="ghost" size="sm" data-testid="nav-login" className="text-[#A1A1AA] hover:text-white hover:bg-[#1F1F23]">
              Iniciar sesión
            </Button>
          </Link>
          <Link to="/register">
            <Button size="sm" data-testid="nav-register" className="bg-[#5E6AD2] hover:bg-[#4F58B8] text-white">
              Comenzar ya
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-80"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 25% 20%, rgba(94,106,210,0.22), transparent 60%), radial-gradient(ellipse 50% 40% at 80% 60%, rgba(249,115,22,0.18), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#A78BFA] bg-[#A78BFA]/10 border border-[#A78BFA]/30 rounded-full px-3 py-1 mb-6">
            <Clock size={10} weight="fill" /> IA conversacional 24/7
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.95] mb-6">
            Cierra más ventas{" "}
            <span className="relative">
              mientras duermes
              <span className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-[#5E6AD2] to-[#F97316] rounded-full" />
            </span>
          </h1>
          <p className="text-lg text-[#D4D4D8] leading-relaxed mb-8 max-w-xl">
            Un agente de ventas inteligente en WhatsApp que atiende clientes, cierra pedidos y agenda
            citas automáticamente. Sin perder una venta, nunca.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register">
              <Button
                size="lg"
                data-testid="hero-cta-primary"
                className="bg-[#5E6AD2] hover:bg-[#4F58B8] text-white h-13 px-6"
              >
                Comenzar ya <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button
                size="lg"
                variant="outline"
                data-testid="hero-cta-secondary"
                className="border-[#27272A] bg-transparent hover:bg-[#1F1F23] text-white h-13 px-6"
              >
                Iniciar sesión
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-xs text-[#71717A]">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => <Star key={i} size={12} weight="fill" className="text-amber-400" />)}
              <span className="ml-1">4.9/5</span>
            </div>
            <span>·</span>
            <span>Setup en 10 min</span>
            <span>·</span>
            <span>Sin tarjeta</span>
          </div>
        </div>
        <div>
          <HeroChat />
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#27272A] bg-[#09090B] py-12 px-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
        <div>
          <Link to="/" className="flex items-center gap-2.5 mb-4">
            <LogoMark size={32} />
            <div className="font-display font-medium">
              <span className="text-[#5E6AD2]">Wazco</span> <span className="text-[#F97316]">Express</span>
            </div>
          </Link>
          <p className="text-xs text-[#71717A] leading-relaxed">
            Agente de ventas inteligente para WhatsApp. Automatiza pedidos, citas y seguimiento con IA.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-3">Producto</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#beneficios" className="text-[#A1A1AA] hover:text-white">Beneficios</a></li>
            <li><a href="#como-funciona" className="text-[#A1A1AA] hover:text-white">Cómo funciona</a></li>
            <li><a href="#demo" className="text-[#A1A1AA] hover:text-white">Demo</a></li>
            <li><a href="#planes" className="text-[#A1A1AA] hover:text-white">Planes</a></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-3">Empresa</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#contacto" className="text-[#A1A1AA] hover:text-white">Contacto</a></li>
            <li><Link to="/register" className="text-[#A1A1AA] hover:text-white">Crear cuenta</Link></li>
            <li><Link to="/login" className="text-[#A1A1AA] hover:text-white">Iniciar sesión</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-3">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><a href="/legal/terminos.pdf" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-white" data-testid="footer-terms">Términos y Condiciones</a></li>
            <li><a href="/legal/privacidad.pdf" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-white" data-testid="footer-privacy">Política de Privacidad</a></li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 pt-8 border-t border-[#27272A] text-xs text-[#71717A]">
        <div>© {new Date().getFullYear()} Wazco Express. Todos los derechos reservados.</div>
        <div>Hecho con 💜 para negocios que venden más.</div>
      </div>
    </footer>
  );
}

// ------------------------ Page ------------------------
export default function Landing() {
  return (
    <div className="min-h-screen bg-[#09090B] text-[#FAFAFA]" data-testid="landing-page">
      <Nav />
      <Hero />
      <Benefits />
      <HowItWorks />
      <DashboardPreview />
      <InteractiveDemo />
      <LaunchOffer />
      <Pricing />
      <ContactForm />
      <Footer />
    </div>
  );
}
